/**
 * Hook to query the timeline subcollection of an incident.
 * Returns both evidence (flagged) and context (non-flagged) messages
 * for rendering a complete conversation timeline.
 */

import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { parseTimestamp, type TimelineMessage } from '@/lib/incidentUtils';

interface UseIncidentTimelineReturn {
  timeline: TimelineMessage[];
  loading: boolean;
  error: string | null;
}

export function useIncidentTimeline(
  connectionId: string | undefined,
  conversationId: string | undefined,
  incidentId: string | undefined,
): UseIncidentTimelineReturn {
  const [timeline, setTimeline] = useState<TimelineMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!connectionId || !conversationId || !incidentId) {
      setTimeline([]);
      setLoading(false);
      return;
    }

    const timelinePath = `connections/${connectionId}/conversations/${conversationId}/incidents/${incidentId}/timeline`;
    const timelineRef = collection(db, timelinePath);
    const timelineQuery = query(timelineRef, orderBy('message_timestamp', 'asc'));

    const unsubscribe = onSnapshot(
      timelineQuery,
      (snapshot) => {
        const messages: TimelineMessage[] = snapshot.docs.map((d) => {
          const data = d.data();
          return {
            message_id: data.message_id ?? d.id,
            timestamp: parseTimestamp(data.timestamp, new Date())!,
            message_timestamp: data.message_timestamp ?? null,
            content: data.content ?? '',
            messageType: data.messageType ?? 'text',
            pushName: data.pushName ?? 'Unknown',
            fromMe: data.fromMe ?? false,
            context_type: data.context_type === 'evidence' ? 'evidence' : 'context',
            severity_score: data.severity_score ?? 0,
            flagged_categories: data.flagged_categories ?? [],
            mediaUrl: data.mediaUrl ?? '',
            event_explanation: data.event_explanation ?? '',
          };
        });
        setTimeline(messages);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error in timeline listener:', err);
        setError(`Failed to load timeline: ${err.message}`);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [connectionId, conversationId, incidentId]);

  return { timeline, loading, error };
}
